import { Button } from "@material-ui/core";
import React, { useRef, useEffect, useState } from "react";
import api from "../../services/api";
import { Typography } from "@material-ui/core";
import { useTheme, makeStyles } from "@material-ui/core/styles";
import { isLikelyNgrokPublicUrl, fetchPublicMediaBlobUrl } from "../../utils/ngrokPublicMedia";

const LS_NAME = "audioMessageRate";

// ✅ CORREÇÃO: Estilos específicos para controlar tamanho e aparência
const useStyles = makeStyles((theme) => ({
  audioContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '380px', // ✅ Limita largura máxima
    minWidth: '300px',  // ✅ Largura mínima
    padding: 0,
    margin: 0,
    backgroundColor: 'transparent',
    border: 'none'
  },
  audioPlayerContainer: {
    position: 'relative',
    width: '100%',
    height: '40px', // ✅ Altura fixa
    marginBottom: theme.spacing(1)
  },
  audioPlayer: {
    width: '100%',
    height: '40px', // ✅ Altura específica
    outline: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    // ✅ Remove aparência padrão problemática
    '&::-webkit-media-controls-panel': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-media-controls-current-time-display, &::-webkit-media-controls-time-remaining-display': {
      fontSize: '12px'
    }
  },
  controlsContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(1)
  },
  waveformBar: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: 2,
    height: 24,
    marginBottom: theme.spacing(0.5),
  },
  waveformBarItem: {
    width: 3,
    minHeight: 4,
    borderRadius: 2,
    backgroundColor: theme.palette.primary.main,
    opacity: 0.8,
  },
  transcriptionContainer: {
    width: '100%',
    marginTop: theme.spacing(1),
    // ✅ CORREÇÃO: Centralizar o botão
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  transcriptionText: {
    fontSize: '0.875rem',
    lineHeight: 1.4,
    wordBreak: 'break-word',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    width: '100%',
    boxSizing: 'border-box'
  },
  transcribeButton: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1),
    minWidth: 'auto',
    height: '32px',
    // ✅ CORREÇÃO: Centralizar o botão
    alignSelf: 'center'
  },
  rateButton: {
    position: 'absolute',
    top: '2px',
    right: '8px',
    fontSize: '0.7rem',
    minWidth: 'auto',
    padding: '1px 6px',
    height: '18px',
    backgroundColor: 'rgba(0,0,0,0.6)',
    color: 'white',
    borderRadius: '9px',
    zIndex: 10,
    lineHeight: 1,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.8)'
    },
    // ✅ Remover estilos de botão padrão
    border: 'none',
    textTransform: 'none',
    boxShadow: 'none'
  }
}));

const WAVEFORM_BARS = 32;

const AudioModal = ({ url, message, disableTranscription = false }) => {
  const theme = useTheme();
  const classes = useStyles();
  const audioRef = useRef(null);
  const [audioRate, setAudioRate] = useState(
    parseFloat(localStorage.getItem(LS_NAME) || "1")
  );
  const [showButtonRate, setShowButtonRate] = useState(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const [transcription, setTranscription] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [waveform, setWaveform] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackBlobUrl, setPlaybackBlobUrl] = useState(null);
  const playbackBlobUrlRef = useRef(null);
  const [ngrokPlaybackUrl, setNgrokPlaybackUrl] = useState(null);
  const ngrokPlaybackUrlRef = useRef(null);

  const body = message?.body ?? "";
  const transcrito = message?.transcrito ?? false;

  const rawUrl = url ? String(url).split("?")[0] : "";
  const isOggLike = /\.(ogg|oga|opus)(\?|$)/i.test(rawUrl);
  const usePlaybackEndpoint = isIOS && message?.id && isOggLike;

  useEffect(() => {
    if (!usePlaybackEndpoint) {
      setPlaybackBlobUrl(null);
      return;
    }
    api
      .get(`/messages/${message.id}/playback-audio`, { responseType: "blob" })
      .then((res) => {
        if (playbackBlobUrlRef.current) {
          URL.revokeObjectURL(playbackBlobUrlRef.current);
          playbackBlobUrlRef.current = null;
        }
        const blobUrl = URL.createObjectURL(res.data);
        playbackBlobUrlRef.current = blobUrl;
        setPlaybackBlobUrl(blobUrl);
      })
      .catch(() => setPlaybackBlobUrl(null));
    return () => {
      if (playbackBlobUrlRef.current) {
        URL.revokeObjectURL(playbackBlobUrlRef.current);
        playbackBlobUrlRef.current = null;
      }
      setPlaybackBlobUrl(null);
    };
  }, [usePlaybackEndpoint, usePlaybackEndpoint ? message?.id : null]);

  useEffect(() => {
    if (usePlaybackEndpoint || !url) {
      setNgrokPlaybackUrl(null);
      return;
    }
    const raw = String(url);
    if (!isLikelyNgrokPublicUrl(raw)) {
      setNgrokPlaybackUrl(null);
      return;
    }
    let cancelled = false;
    fetchPublicMediaBlobUrl(raw)
      .then((blobUrl) => {
        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        if (ngrokPlaybackUrlRef.current) {
          URL.revokeObjectURL(ngrokPlaybackUrlRef.current);
          ngrokPlaybackUrlRef.current = null;
        }
        ngrokPlaybackUrlRef.current = blobUrl;
        setNgrokPlaybackUrl(blobUrl);
      })
      .catch(() => setNgrokPlaybackUrl(null));
    return () => {
      cancelled = true;
      if (ngrokPlaybackUrlRef.current) {
        URL.revokeObjectURL(ngrokPlaybackUrlRef.current);
        ngrokPlaybackUrlRef.current = null;
      }
      setNgrokPlaybackUrl(null);
    };
  }, [url, usePlaybackEndpoint]);

  useEffect(() => {
    if (usePlaybackEndpoint && playbackBlobUrl && audioRef.current) {
      audioRef.current.src = playbackBlobUrl;
      audioRef.current.load();
    }
  }, [usePlaybackEndpoint, playbackBlobUrl]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = audioRate;
      localStorage.setItem(LS_NAME, audioRate);
    }
  }, [audioRate]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onplaying = () => {
        setShowButtonRate(true);
      };
      audioRef.current.onpause = () => {
        setShowButtonRate(false);
      };
      audioRef.current.onended = () => {
        setShowButtonRate(false);
      };
    }
  }, []);

  // Waveform: usar message.dataJson?.waveform se existir, senão gerar do áudio
  useEffect(() => {
    let cancelled = false;
    const dataJson = message?.dataJson;
    if (dataJson && typeof dataJson === 'object' && Array.isArray(dataJson.waveform)) {
      setWaveform(dataJson.waveform.slice(0, WAVEFORM_BARS));
      return;
    }
    if (typeof dataJson === 'string') {
      try {
        const parsed = JSON.parse(dataJson);
        if (Array.isArray(parsed?.waveform)) {
          setWaveform(parsed.waveform.slice(0, WAVEFORM_BARS));
          return;
        }
      } catch (_) {}
    }
    let ctx;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (_) {
      setWaveform(Array(WAVEFORM_BARS).fill(8));
      return;
    }
    fetch(url, {
      mode: "cors",
      headers: isLikelyNgrokPublicUrl(String(url))
        ? { "ngrok-skip-browser-warning": "true" }
        : {},
    })
      .then((res) => res.arrayBuffer())
      .then((buf) => ctx.decodeAudioData(buf))
      .then((decoded) => {
        if (cancelled || !decoded) return;
        const ch = decoded.getChannelData(0);
        const len = ch.length;
        const seg = Math.floor(len / WAVEFORM_BARS);
        const peaks = [];
        for (let i = 0; i < WAVEFORM_BARS; i++) {
          let peak = 0;
          const start = i * seg;
          for (let j = 0; j < seg && start + j < len; j++) {
            const v = Math.abs(ch[start + j]);
            if (v > peak) peak = v;
          }
          peaks.push(Math.min(255, Math.round((peak / 1) * 255) || 8));
        }
        setWaveform(peaks);
      })
      .catch(() => {
        if (!cancelled) setWaveform(Array(WAVEFORM_BARS).fill(8));
      });
    return () => { cancelled = true; };
  }, [url, message?.dataJson]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onDurationChange = () => setDuration(el.duration);
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('durationchange', onDurationChange);
    setDuration(el.duration || 0);
    setCurrentTime(el.currentTime || 0);
    return () => {
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('durationchange', onDurationChange);
    };
  }, [url]);

  const toggleRate = () => {
    let newRate = null;

    switch (audioRate) {
      case 0.5:
        newRate = 1;
        break;
      case 1:
        newRate = 1.5;
        break;
      case 1.5:
        newRate = 2;
        break;
      case 2:
        newRate = 0.5;
        break;
      default:
        newRate = 1;
        break;
    }

    setAudioRate(newRate);
  };

  const displayUrl =
    usePlaybackEndpoint && playbackBlobUrl
      ? playbackBlobUrl
      : ngrokPlaybackUrl
        ? ngrokPlaybackUrl
        : url
          ? rawUrl || String(url).split("?")[0]
          : null;
  const displayType = usePlaybackEndpoint && playbackBlobUrl ? "audio/mpeg" : null;

  const getAudioSource = () => {
    if (!displayUrl) return null;
    if (displayType) return <source src={displayUrl} type={displayType} />;
    const lower = displayUrl.toLowerCase();
    let mimeType = "audio/ogg";
    if (lower.endsWith(".mp3")) mimeType = "audio/mpeg";
    else if (lower.endsWith(".m4a") || lower.endsWith(".mp4")) mimeType = "audio/mp4";
    else if (lower.endsWith(".wav")) mimeType = "audio/wav";
    else if (lower.endsWith(".oga") || lower.endsWith(".ogg") || lower.endsWith(".opus")) mimeType = "audio/ogg";
    return <source src={displayUrl} type={mimeType} />;
  };

  const handleTranscribe = async () => {
    setIsTranscribing(true);
    try {
      let audioData = {
        wid: message.wid,
      };

      const { data } = await api.post(`/message/transcribeAudio`, audioData);

      if (data) {
        setTranscription(data);
      } else {
        console.error("Invalid response data:", data);
      }
    } catch (error) {
      console.error("Erro ao transcrever áudio:", error);
    } finally {
      setIsTranscribing(false);
    }
  };

  return (
    <div className={classes.audioContainer}>
      {waveform && waveform.length > 0 && (
        <div className={classes.waveformBar}>
          {waveform.map((val, i) => {
            const pct = duration > 0 ? (currentTime / duration) * waveform.length : 0;
            const isPlayed = i < pct;
            const h = Math.max(4, Math.round((val / 255) * 20));
            return (
              <div
                key={i}
                className={classes.waveformBarItem}
                style={{
                  height: h,
                  opacity: isPlayed ? 0.5 : 0.9,
                }}
              />
            );
          })}
        </div>
      )}
      {/* ✅ Container do player igual ao original */}
      <div className={classes.audioPlayerContainer}>
        <audio 
          ref={audioRef} 
          controls 
          className={classes.audioPlayer}
          preload="metadata"
        >
          {getAudioSource()}
        </audio>
        
        {/* ✅ Botão de velocidade igual ao original */}
        {showButtonRate && (
          <Button
            className={classes.rateButton}
            onClick={toggleRate}
            size="small"
            disableRipple
          >
            {audioRate}x
          </Button>
        )}
      </div>

      {/* ✅ ÚNICA MUDANÇA: Container de controles com botão centralizado */}
      <div className={classes.controlsContainer}>
        {!disableTranscription && (
          <div className={classes.transcriptionContainer}>
            {!transcrito ? (
              transcription ? (
                <Typography className={classes.transcriptionText} variant="body2">
                  <strong>Transcrição:</strong> {transcription}
                </Typography>
              ) : (
                <Button
                  onClick={handleTranscribe}
                  variant="contained"
                  className={classes.transcribeButton}
                  disabled={isTranscribing}
                  style={{
                    backgroundColor: isTranscribing
                      ? "#ccc"
                      : theme.palette.primary.main,
                    color: "#fff",
                  }}
                >
                  {isTranscribing ? "Transcrevendo..." : "Transcrever"}
                </Button>
              )
            ) : (
              <Typography className={classes.transcriptionText} variant="body2">
                <strong>Transcrição:</strong> {body}
              </Typography>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioModal;